import * as THREE from 'three';
import {
  bevelBox, extrudeShape, filletShape, chamferCyl, chamferCone,
  tubeY, capScrew, rollPin, stippleField, loftSection,
} from './Bevel.js';
import { place } from './Builder.js';
import { MARK } from '../GunMaterials.js';

/**
 * Shared sub-assemblies. Weapon-space convention for every gun in this project:
 *
 *   -Z  muzzle / forward        +Z  buttstock / rearward
 *   +Y  top (rail, sights)      -Y  magazine, grip
 *   +X  right (ejection port)   -X  left (selector, bolt catch)
 *
 * The origin sits on the bore axis at the rear face of the upper receiver.
 */

/* ------------------------------------------------------------------ *
 * Orientation helpers
 * ------------------------------------------------------------------ */

/** Extrude a side-view profile — points are [z, y] — across the X axis. */
export function sideProfile(pts, radius, width, bevel = 0.0006, seg = 3) {
  const g = extrudeShape(filletShape(pts, radius, seg), width, { bevel });
  g.rotateY(-Math.PI / 2);
  return g;
}

/** Extrude a front-view profile — points are [x, y] — along Z. */
export function frontProfile(pts, radius, depth, bevel = 0.0006, seg = 3) {
  return extrudeShape(filletShape(pts, radius, seg), depth, { bevel });
}

/** Chamfered cylinder whose axis lies along Z. */
export function cylZ(r, len, chamfer = 0.0005, seg = 22, opts) {
  return chamferCyl(r, len, chamfer, seg, opts).rotateX(Math.PI / 2);
}

/** Cone frustum along Z: `rFront` at -Z, `rBack` at +Z. */
export function coneZ(rFront, rBack, len, chamfer = 0.0005, seg = 22) {
  return chamferCone(rBack, rFront, len, chamfer, seg).rotateX(-Math.PI / 2);
}

/** Tube along Z. */
export function tubeZ(rOut, rIn, len, chamfer = 0.0005, seg = 24) {
  return tubeY(rOut, rIn, len, chamfer, seg).rotateX(Math.PI / 2);
}

/* ------------------------------------------------------------------ *
 * Roll marks
 * ------------------------------------------------------------------ */

/** A flat decal quad carrying atlas UVs, oriented onto one of the gun's faces. */
export function decal(markName, width, plane, x, y, z, opts = {}) {
  const m = MARK[markName];
  if (!m) return null;
  const h = width / m.aspect;
  const g = new THREE.PlaneGeometry(width, h, 1, 1);
  const uv = g.attributes.uv;
  for (let i = 0; i < uv.count; i++) {
    // `mirror` flips U so a mark printed on the opposite flank still reads
    // left-to-right instead of appearing reversed.
    const u = opts.mirror ? 1 - uv.getX(i) : uv.getX(i);
    uv.setXY(i, m.u0 + u * (m.u1 - m.u0), m.v0 + uv.getY(i) * (m.v1 - m.v0));
  }
  uv.needsUpdate = true;
  const roll = opts.roll ?? 0;
  let mat;
  switch (plane) {
    case 'left': mat = place(x, y, z, 0, -Math.PI / 2, roll); break;
    case 'right': mat = place(x, y, z, 0, Math.PI / 2, roll); break;
    case 'top': mat = place(x, y, z, -Math.PI / 2, 0, roll); break;
    case 'bottom': mat = place(x, y, z, Math.PI / 2, 0, roll); break;
    default: mat = place(x, y, z, 0, 0, roll);
  }
  g.applyMatrix4(mat);
  return g;
}

/* ------------------------------------------------------------------ *
 * Fasteners
 * ------------------------------------------------------------------ */

export function screwY(x, y, z, r = 0.0021, headH = 0.0015) {
  return capScrew(r, headH, 0.003).translate(x, y, z);
}

export function screwX(x, y, z, sign = 1, r = 0.0021, headH = 0.0015) {
  const g = capScrew(r, headH, 0.003);
  g.rotateZ(-sign * Math.PI / 2);
  return g.translate(x, y, z);
}

/** Takedown / pivot pin heads showing on both sides of a receiver. */
export function crossPin(y, z, r = 0.0026, halfWidth = 0.0192) {
  const out = [];
  for (const s of [-1, 1]) {
    const g = rollPin(r, 0.0018);
    g.rotateZ(-s * Math.PI / 2);
    out.push(g.translate(s * halfWidth, y, z));
  }
  return out;
}

/** A run of knurling ribs around a cylinder whose axis is +Y (before placement). */
export function knurl(radius, height, count = 16, depth = 0.0009) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2;
    const rib = bevelBox(depth, height, depth * 1.15, depth * 0.22);
    rib.rotateY(-a);
    out.push(rib.translate(Math.sin(a) * radius, 0, Math.cos(a) * radius));
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Optic: Aimpoint-style micro red dot on a QD riser
 * ------------------------------------------------------------------ */

export function redDotOptic(B, bone, opts = {}) {
  const railTop = opts.railTop ?? 0.0296;
  const axis = opts.axis ?? 0.0655;
  const z = opts.z ?? -0.055;
  const bodyR = 0.0172;
  const tubeLen = 0.0510;
  const bev = 0.0005;

  const bossBottom = axis - bodyR - 0.0053;  // underside of the optic's flat boss
  const mountH = bossBottom - railTop;

  // --- QD riser -------------------------------------------------------------
  B.add(sideProfile([
    [z - 0.0215, 0], [z + 0.0215, 0], [z + 0.0215, mountH], [z + 0.0125, mountH],
    [z + 0.0125, mountH * 0.34], [z - 0.0125, mountH * 0.34],
    [z - 0.0125, mountH], [z - 0.0215, mountH],
  ], 0.0028, 0.0252, 0.0011), 'optic', bone, place(0, railTop, 0), { edgeWear: 0.8 });

  // rail clamp jaw + throw lever on the left
  B.add(bevelBox(0.0072, 0.0130, 0.0310, 0.0005), 'optic', bone,
    place(-0.0162, railTop + 0.0048, z), { edgeWear: 0.65 });
  B.add(sideProfile([
    [z - 0.013, 0], [z + 0.013, 0], [z + 0.011, 0.0042], [z - 0.011, 0.0042],
  ], 0.0008, 0.0060, 0.0005), 'optic', bone,
    place(-0.0212, railTop + 0.0042, 0), { edgeWear: 1.0 });
  B.add(cylZ(0.0026, 0.0100, 0.0004, 12), 'bare', bone,
    place(-0.0180, railTop + 0.0062, z + 0.0105, 0, Math.PI / 2, 0), { wear: 0.45 });
  for (const dz of [-0.0125, 0.0125]) {
    B.add(screwY(0, railTop + mountH - 0.0004, z + dz, 0.0027, 0.0016), 'bare', bone, null, { wear: 0.55 });
  }

  // --- optic body -----------------------------------------------------------
  const body = [];
  body.push(tubeZ(bodyR, bodyR - 0.0024, tubeLen, 0.0009, 28));
  const bellRear = tubeZ(bodyR + 0.0024, bodyR - 0.0020, 0.0078, 0.0007, 28);
  bellRear.translate(0, 0, tubeLen / 2 - 0.0039);
  const bellFront = tubeZ(bodyR + 0.0024, bodyR - 0.0020, 0.0078, 0.0007, 28);
  bellFront.translate(0, 0, -tubeLen / 2 + 0.0039);
  body.push(bellRear, bellFront);
  body.push(bevelBox(0.0248, 0.0112, 0.0420, 0.0008).translate(0, -bodyR - 0.0004, 0));
  B.add(body, 'optic', bone, place(0, axis, z), { edgeWear: 0.5 });
  // matte black inner liner — without it the lit tube wall washes out the sight picture
  B.add(tubeZ(bodyR - 0.0024, bodyR - 0.0046, tubeLen - 0.0050, 0.0004, 26), 'rubber', bone,
    place(0, axis, z), { noAO: true });

  // --- turrets --------------------------------------------------------------
  const turret = (m) => {
    B.add(chamferCyl(0.0074, 0.0092, 0.0006, 18), 'optic', bone, m, { edgeWear: 0.6 });
    B.add(chamferCyl(0.0057, 0.0044, 0.0005, 18), 'optic', bone,
      new THREE.Matrix4().multiplyMatrices(m, place(0, 0.0066, 0)), { edgeWear: 0.7 });
    B.add(knurl(0.0057, 0.0044, 14, 0.0008), 'optic', bone,
      new THREE.Matrix4().multiplyMatrices(m, place(0, 0.0066, 0)), { wear: 0.35 });
  };
  turret(place(0, axis + bodyR + 0.0026, z + 0.0165));
  turret(place(bodyR + 0.0026, axis, z + 0.0165, 0, 0, -Math.PI / 2));

  // battery cap on the left
  const capM = place(-bodyR - 0.0022, axis, z + 0.0165, 0, 0, Math.PI / 2);
  B.add(chamferCyl(0.0086, 0.0064, 0.0007, 20), 'optic', bone, capM, { edgeWear: 0.75 });
  B.add(knurl(0.0086, 0.0064, 20, 0.0009), 'optic', bone, capM, { wear: 0.4 });
  // brightness rotary, top rear
  const rotM = place(0, axis + bodyR + 0.0018, z - 0.0170);
  B.add(chamferCyl(0.0062, 0.0080, 0.0006, 18), 'optic', bone, rotM, { edgeWear: 0.55 });
  B.add(knurl(0.0062, 0.0080, 16, 0.0009), 'optic', bone, rotM, { wear: 0.3 });

  // --- lenses ---------------------------------------------------------------
  const lensR = bodyR - 0.0026;
  const front = new THREE.CircleGeometry(lensR, 28).rotateY(Math.PI);
  B.add(front, 'lens', bone, place(0, axis, z - tubeLen / 2 + 0.0058));
  B.add(new THREE.CircleGeometry(lensR, 28), 'lens', bone,
    place(0, axis, z + tubeLen / 2 - 0.0058));

  // --- reticle --------------------------------------------------------------
  // Sits exactly on the optical axis. The ADS pose aligns the view camera to that
  // same axis, so this projects to the pixel centre of the screen.
  const sightZ = z - tubeLen / 2 + 0.0075;
  B.add(new THREE.PlaneGeometry(0.0080, 0.0080), 'reticle', bone,
    place(0, axis, sightZ), { keepUV: true });

  B.add(decal('optic_mark', 0.028, 'left', -bodyR - 0.0006, axis + 0.0060, z - 0.004),
    'marks', bone, null, { keepUV: true });

  return { axis, z, sightZ };
}

/* ------------------------------------------------------------------ *
 * Folding back-up iron sights
 * ------------------------------------------------------------------ */

export function backupSights(B, bone, opts = {}) {
  const railTop = opts.railTop ?? 0.0296;
  const bev = 0.0004;
  const rz = opts.rearZ ?? -0.012;
  const fz = opts.frontZ ?? -0.392;

  /*
   * Both sights are FOLDED. They are back-up irons under a red dot, and a folded
   * blade is what a rifle with an optic mounted actually carries — it is also what
   * keeps the front sight out of the optic's sight picture, which the critique
   * read as "the scope is rendering the viewmodel's own geometry". The blade now
   * lies forward along its housing instead of standing 2 mm proud of it.
   */
  /*
   * The sight bodies are ANODISED, not bare steel.
   *
   * They used to be `bare` — a metalness-1 surface at roughness ~0.1 sitting on
   * top of the rail with an uninterrupted view of the sky. Measured on
   * `wpn_closeup`: the rear-sight housing printed sRGB 200-214 and was the
   * lightest object in the frame by a wide margin, a white slab on a dark
   * rifle. An ablation confirmed it was env-dominated (killing the viewmodel
   * IBL for `bare` alone took it from 200 to 74), i.e. it was a mirror pointed
   * at a cloud — which is exactly the "reads as nickel" defect.
   *
   * A real BUIS body is type-III anodised aluminium or a phosphated steel
   * casting. Only the hinge pins, detents and screws are bright. FINDINGS'
   * bare-metal list — barrel, bolt carrier, charging handle, forward assist,
   * ejection-port lip, pins, sling swivels, muzzle crown — does not include
   * the sight housings, and they are the wrong place to spend the frame's
   * brightest value.
   */
  const foldedLeaf = (z, len, width, h0) => {
    B.add(sideProfile([
      [z - len, h0], [z + len * 0.86, h0], [z + len * 0.86, h0 + 0.0016], [z - len, h0 + 0.0016],
    ], 0.0005, width, bev), 'metal', bone, place(0, railTop, 0), { edgeWear: 0.95 });
  };

  // rear sight: housing + folded aperture leaf + windage drum
  B.add(sideProfile([
    [rz - 0.0105, 0], [rz + 0.0105, 0], [rz + 0.0105, 0.0040], [rz - 0.0105, 0.0040],
  ], 0.0009, 0.0205, bev), 'metal', bone, place(0, railTop, 0), { edgeWear: 0.6 });
  foldedLeaf(rz + 0.0010, 0.0086, 0.0132, 0.0040);
  B.add(cylZ(0.0026, 0.0212, 0.0004, 12).rotateY(Math.PI / 2), 'bare', bone,
    place(0, railTop + 0.0022, rz - 0.0086), { wear: 0.45 });   // hinge pin
  for (const s of [-1, 1]) {
    B.add(screwX(s * 0.0104, railTop + 0.0021, rz + 0.005, s, 0.0018, 0.0012), 'bare', bone, null, { wear: 0.5 });
  }

  // front sight: housing + folded post leaf
  B.add(sideProfile([
    [fz - 0.0085, 0], [fz + 0.0085, 0], [fz + 0.0085, 0.0038], [fz - 0.0085, 0.0038],
  ], 0.0009, 0.0185, bev), 'metal', bone, place(0, railTop, 0), { edgeWear: 0.6 });
  foldedLeaf(fz + 0.0016, 0.0076, 0.0118, 0.0038);
  B.add(cylZ(0.0024, 0.0192, 0.0004, 12).rotateY(Math.PI / 2), 'bare', bone,
    place(0, railTop + 0.0020, fz - 0.0076), { wear: 0.45 });
}

/* ------------------------------------------------------------------ *
 * STANAG magazine
 * ------------------------------------------------------------------ */

export function stanagMag(B, bone, opts = {}) {
  const mat = opts.mat ?? 'polymer';
  const W = opts.width ?? 0.0247;
  const D = opts.depth ?? 0.0330;
  const L = opts.length ?? 0.192;
  const bev = 0.0007;
  const curve = opts.curve ?? 0.26;   // total rake over the length, radians
  const segs = 8;
  const parts = [];
  // Where the feed lips sit in weapon space.
  const AT = opts.at ?? new THREE.Matrix4();

  // Segment transforms follow an arc so the "banana" is real geometry, not a shear.
  // The floorplate ends up FORWARD of the feed lips, as on a real curved magazine.
  const R = L / curve;
  const segAt = (t) => {
    const a = t * curve;
    return new THREE.Matrix4().multiplyMatrices(AT,
      place(0, -Math.sin(a) * R, -(1 - Math.cos(a)) * R, a, 0, 0));
  };
  const at = (m) => new THREE.Matrix4().multiplyMatrices(AT, m);

  for (let i = 0; i < segs; i++) {
    const tc = (i + 0.5) / segs;
    const seg = bevelBox(W, (L / segs) * 1.55, D, bev, { corner: 0.0030 });
    parts.push(seg.applyMatrix4(segAt(tc)));
  }
  parts.push(bevelBox(W + 0.0018, 0.0105, D + 0.0018, 0.0007).applyMatrix4(at(place(0, -0.0042, 0))));
  const fpM = segAt(1);
  parts.push(bevelBox(W + 0.0024, 0.0090, D + 0.0024, 0.0009).applyMatrix4(
    new THREE.Matrix4().multiplyMatrices(fpM, place(0, 0.0045, 0))));
  parts.push(bevelBox(W + 0.0038, 0.0034, D + 0.0038, 0.0010).applyMatrix4(
    new THREE.Matrix4().multiplyMatrices(fpM, place(0, 0.0008, 0))));
  B.add(parts, mat, bone, null, { edgeWear: 0.28 });

  // grip ribs down both sides
  for (let i = 0; i < 5; i++) {
    const t = 0.22 + i * 0.145;
    const m = segAt(t);
    for (const sx of [-1, 1]) {
      B.add(bevelBox(0.0017, 0.0080, D - 0.0070, 0.0004), mat, bone,
        new THREE.Matrix4().multiplyMatrices(m, place(sx * (W / 2 + 0.0004), 0, 0)), { edgeWear: 0.55 });
    }
  }
  // witness holes down the right side
  for (let i = 0; i < 4; i++) {
    const m = segAt(0.28 + i * 0.17);
    B.add(chamferCyl(0.0027, 0.0020, 0.0004, 12).rotateZ(Math.PI / 2), 'bare', bone,
      new THREE.Matrix4().multiplyMatrices(m, place(W / 2 - 0.0007, 0, -0.0060)), { wear: 0.2 });
  }

  // a loaded round showing at the feed lips
  B.add(coneZ(0.0028, 0.0043, 0.0130, 0.0003, 14), 'brass', bone, at(place(0, -0.0012, -0.0050)));
  B.add(cylZ(0.0043, 0.0125, 0.0004, 14), 'brass', bone, at(place(0, -0.0026, 0.0058)));

  B.add(decal('mag_mark', 0.020, 'left', -W / 2 - 0.0007, -0.032, 0.0),
    'marks', bone, AT, { keepUV: true });

  return { width: W, depth: D, length: L, curve, segAt };
}

/* ------------------------------------------------------------------ *
 * Pistol grip with real geometric stippling
 * ------------------------------------------------------------------ */

export function pistolGrip(B, bone, rng, opts = {}) {
  const mat = opts.mat ?? 'polymer';
  const tilt = opts.tilt ?? 0.42;
  const len = opts.length ?? 0.106;
  const M = place(0, opts.y ?? -0.026, opts.z ?? 0.004, -tilt, 0, 0);
  const wAt = (t) => 0.0322 - 0.0050 * Math.sin(t * Math.PI) + 0.0042 * t * t;
  // three shallow finger swells along the front strap, built into the loft so they
  // are part of the surface rather than cylinders stuck onto it
  const swell = (t) => 0.0016 * Math.sin(t * Math.PI * 3.2 - 0.5);
  const dAt = (t) => 0.0352 - 0.0080 * Math.sin(t * Math.PI * 0.9) + 0.0060 * t * t + swell(t);

  // One continuous lofted body: stacked boxes leave scalloped waists at the joins,
  // which at 30 cm reads as a stack of loose plates rather than a moulded grip.
  const body = loftSection((t) => ({ w: wAt(t), h: dAt(t), r: 0.0068 }), len, 22, 24);
  body.rotateX(Math.PI / 2);       // length now runs down -Y, depth along +Z
  const parts = [body];
  parts.push(bevelBox(0.0300, 0.0142, 0.0212, 0.0012, { corner: 0.0050 }).translate(0, 0.0026, 0.0196));
  parts.push(bevelBox(0.0348, 0.0062, 0.0382, 0.0012, { corner: 0.0058 }).translate(0, -len - 0.0014, 0.0016));
  B.add(parts, mat, bone, M, { edgeWear: 0.22 });

  // stippling: side panels (normal ±X) and the backstrap (normal +Z)
  for (const sx of [-1, 1]) {
    const s = stippleField(0.0240, len * 0.72, rng, {
      pitch: 0.00295, height: 0.00030, jitter: 0.28,
      mask: (u, v) => Math.abs(u - 0.5) < 0.47 - 0.14 * Math.abs(v - 0.5),
    });
    s.rotateY(sx * Math.PI / 2);
    B.add(s, mat, bone, new THREE.Matrix4().multiplyMatrices(M,
      place(sx * (wAt(0.5) / 2 - 0.0006), -len * 0.47, 0.0010)), {});
  }
  const back = stippleField(0.0240, len * 0.62, rng, { pitch: 0.00295, height: 0.00030, jitter: 0.28 });
  B.add(back, mat, bone, new THREE.Matrix4().multiplyMatrices(M,
    place(0, -len * 0.46, dAt(0.5) / 2 - 0.0006)), {});

  return { tilt, len, matrix: M };
}

/* ------------------------------------------------------------------ *
 * Trigger + guard
 * ------------------------------------------------------------------ */

export function triggerGroup(B, receiverBone, triggerBone, opts = {}) {
  const gz = opts.z ?? -0.038;
  const gy = opts.y ?? -0.030;
  const width = opts.width ?? 0.0118;

  /*
   * The rear of the bow is tucked UP against the receiver.
   *
   * It used to drop to `gy - 0.0105` at the rearmost point and then rake down
   * to `gy - 0.0300`, which is the same space the middle finger's proximal
   * phalanx occupies as it wraps the front strap — "the trigger guard passes
   * through the glove surface at (960,470); zero gap achieved by intersection
   * is not contact". Pulling the rear tang up 7 mm and starting the rake
   * further forward gives the finger somewhere to be. It is also what the real
   * part does: an AR trigger guard meets the grip tang, it does not run down
   * behind it.
   */
  const outer = [
    [gz + 0.0210, gy], [gz + 0.0210, gy - 0.0034], [gz + 0.0128, gy - 0.0264],
    [gz - 0.0180, gy - 0.0335], [gz - 0.0300, gy - 0.0240], [gz - 0.0320, gy - 0.0060], [gz - 0.0300, gy],
  ];
  const inner = [
    [gz + 0.0135, gy - 0.0042], [gz + 0.0135, gy - 0.0090], [gz + 0.0062, gy - 0.0222],
    [gz - 0.0162, gy - 0.0272], [gz - 0.0242, gy - 0.0202], [gz - 0.0252, gy - 0.0062], [gz - 0.0242, gy - 0.0042],
  ];
  const shape = filletShape(outer, 0.0042, 4);
  shape.holes.push(filletShape(inner.slice().reverse(), 0.0042, 4));
  const guard = extrudeShape(shape, width, { bevel: 0.0009, curveSegments: 4 });
  guard.rotateY(-Math.PI / 2);
  B.add(guard, opts.mat ?? 'metal', receiverBone, null, { edgeWear: 0.45 });

  const blade = sideProfile([
    [0.0000, 0.0000], [0.0052, -0.0012], [0.0062, -0.0138], [0.0028, -0.0196],
    [-0.0024, -0.0190], [-0.0036, -0.0128], [-0.0034, -0.0012],
  ], 0.0019, 0.0078, 0.0006);
  B.add(blade, 'bare', triggerBone, null, { edgeWear: 0.95 });
  for (let i = 0; i < 6; i++) {
    B.add(bevelBox(0.0078, 0.0007, 0.0010, 0.0002), 'bare', triggerBone,
      place(0, -0.0058 - i * 0.0022, -0.0033, 0.22, 0, 0), { wear: 0.6 });
  }
  return { guardZ: gz, guardY: gy };
}

/* ------------------------------------------------------------------ *
 * Barrel + A2 flash hider
 * ------------------------------------------------------------------ */

export function barrelAssembly(B, bone, opts = {}) {
  const front = opts.front ?? -0.470;
  const back = opts.back ?? -0.190;
  const rBore = opts.bore ?? 0.00281;
  const len = back - front;
  const parts = [];

  parts.push(cylZ(0.0130, 0.0300, 0.0008, 20).translate(0, 0, back - 0.0150));
  parts.push(tubeZ(0.0092, rBore, len - 0.030, 0.0006, 20).translate(0, 0, (front + back) / 2 - 0.015));
  parts.push(cylZ(0.0082, 0.0080, 0.0006, 20).translate(0, 0, front + 0.0040));
  B.add(parts, 'bare', bone, null, { wear: 0.12 });

  const fhLen = opts.fhLength ?? 0.0505;
  const fhZ = front - fhLen / 2;
  B.add([
    tubeZ(0.0112, rBore + 0.0008, fhLen, 0.0009, 22).translate(0, 0, fhZ),
    cylZ(0.0114, 0.0078, 0.0008, 22).translate(0, 0, front - 0.0040),
  ], 'bare', bone, null, { edgeWear: 0.6, wear: 0.22 });

  // five prongs, four slots — the A2 has a solid bottom so it does not kick up dust
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI * 0.5 + ((i + 0.5) / 5) * Math.PI * 1.64;
    B.add(bevelBox(0.0032, 0.0032, 0.0300, 0.0004), 'bare', bone,
      place(Math.cos(a) * 0.0099, Math.sin(a) * 0.0099, fhZ - 0.0060, 0, 0, -a + Math.PI / 2),
      { edgeWear: 0.7 });
  }
  B.add(tubeZ(0.0106, 0.0082, 0.0024, 0.0004, 20), 'bare', bone,
    place(0, 0, front + 0.0014), { edgeWear: 0.85, wear: 0.35 });

  return { muzzleZ: front - fhLen, fhRadius: 0.0112 };
}
